import React, { useContext } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { Box, Card, CircleLoader, Flex, Icon, Link, Text } from '@driftt/dds'

import ActivityContentToolTip from 'components/ActivityContentTooltip'
import { AccountActivityAccountAvatar } from 'components/Avatars'
import EngagementScoreTooltip from 'components/EngagementScoreTooltip'
import { AccountDrawerContext } from 'context/accountDrawerContext'
import { useAppSelector } from 'hooks/useRedux'
import {
  AccountActivity,
  Activity,
  ActivityContext,
  ActivityEndUser,
  ActivityForContact,
  ActivityType,
  CompanyData
} from 'modules/accountActivity/domain'
import { userActivityTypeToPreviewSnippet } from 'utils/activity'
import { timeSinceUpdated } from 'utils/timeStamp'

export interface RecentActivityCardProps {
  companyData: CompanyData
  name?: string
  lastActivityAt: number
  scoredAccount: AccountActivity
  lastActivityEvent: ActivityForContact & Activity & ActivityContext
  endUserData?: ActivityEndUser
  loading: boolean
  accountData?: any
}

const RecentActivityCard = ({
  companyData,
  lastActivityAt,
  scoredAccount,
  lastActivityEvent,
  endUserData,
  loading,
  accountData
}: RecentActivityCardProps) => {
  const orgUsersById = useAppSelector(({ orgUsersById }) => orgUsersById.users)

  const { setIsOpen: setIsDrawerOpen, setAccountId: setSelectedAccountId } = useContext(AccountDrawerContext)

  if (!lastActivityEvent) {
    return null
  }
  const { preview, action, icon, suffix, href } = userActivityTypeToPreviewSnippet(lastActivityEvent, orgUsersById)
  const name = accountData?.name || companyData.name || companyData.domain

  const { name: contactName, email, locationCity, locationState, id: contactId } = endUserData || {}

  const previewProps = href ? { href, alt: preview, target: '_blank' } : {}

  const formatSuffix = (suffix: string) => {
    return lastActivityEvent.activityType !== ActivityType.SIGNAL ? suffix :
      suffix.split('|||').map((s, i) => <div key={i}>{s}</div>);
  }

  return (
    <Card
      css={{ gap: '$1', padding: '$1 $2', width: '100%', maxWidth: '420px', minHeight: '140px' }}>
      {loading ? (
        <Flex fullHeight fullWidth alignItems="center" justifyContent="center">
          <CircleLoader size="extra-large" />
        </Flex>
      ) : (
        <>
          <Flex gap="half" alignItems="center" justifyContent="space-between" fullWidth>
            <Flex alignItems="center">
              <AccountActivityAccountAvatar
                size="small"
                imageUrl={companyData.logo}
                name={name}
                inCrm={!!accountData}
              />
              <Flex direction="column" gap={0}>
                <ActivityContentToolTip side="bottom" data={companyData} inCrm={!!accountData}>
                  <Box
                    css={{
                      width: 'fit-content',
                      lineHeight: 0,
                      maxWidth: '165px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                    <Link 
                      css={{
                        color: '$blue8',
                        fontWeight: 600,
                        '&:hover': {
                          color: '$blue8',
                          textDecoration: 'underline'
                        }
                      }}
                      onClick={() => {
                        setIsDrawerOpen(true)
                        setSelectedAccountId(scoredAccount.accountId)
                      }}
                    >
                      {name}
                    </Link> 
                  </Box>
                </ActivityContentToolTip>
              </Flex>
            </Flex>
            <Box css={{ minWidth: 'fit-content' }}>
              <EngagementScoreTooltip accountActivity={scoredAccount} boundaryRef={null} />
            </Box>
          </Flex>

          <Flex direction="column" gap="half" fullWidth>
            <Flex
              css={{
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              fullWidth
              alignItems="center"
              wrap="wrap"
              justifyContent="space-between">
              <Flex alignItems="center">
                <Link
                  as={RouterLink}
                  css={{
                    maxWidth: '100px',
                    textOverflow: 'ellipsis',
                    color: '$blue8',
                    '&:hover': {
                      color: '$blue8',
                      textDecoration: 'underline'
                    }
                  }}
                  variant="object"
                  truncate
                  to={`/activity/contacts/${contactId}`}
                  target="_blank"
                  gutterBottom={0}>
                  {contactName || email || 'Site Visitor'}
                </Link>

                <Text 
                  variant={'body'} 
                  css={{
                    maxWidth: '120px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textWrap: 'nowrap'
                  }} >
                  {locationCity && `${locationCity}, `}
                  {locationState && `${locationState}`}
                </Text>
              </Flex>
              <Text css={{ minWidth: '20px' }} variant="body" gutterBottom={0}>
                {timeSinceUpdated(lastActivityAt)} ago
              </Text>
            </Flex>
            <Flex
              gap="half"
              css={{
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              alignItems="center"
              fullWidth>
              <Icon css={{ flexBasis: 'fit-content' }} icon={icon} />
              <Text variant="body">{action}</Text>

              {preview && (
                <Flex
                  gap={0}
                  css={{
                    overflow: 'hidden'
                  }}>
                  <Text css={{ minWidth: 'fit-content' }} variant="metadata">
                    &quot;
                  </Text>
                  <Text
                    css={{
                      textDecoration: 'underline',
                      flexBasis: 'fit-content'
                    }}
                    truncate
                    as={href ? Link : 'p'}
                    {...previewProps}>
                    {preview}
                  </Text>
                  <Text css={{ minWidth: 'fit-content' }} variant="metadata">
                    &quot;
                  </Text>
                </Flex>
              )}
              {suffix && (
                <Text
                  css={{
                    minWidth: 'fit-content'
                  }}>
                  {' '}
                  {formatSuffix(suffix)}
                </Text>
              )}
            </Flex>
            <Flex
              fullWidth
              alignItems="center"
              css={{ borderTop: '1px solid $gray3', marginTop: '$1', paddingTop: '$1' }}>
              <Box
                css={{
                  margin: '0 auto',
                  maxWidth: '90%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                <Link 
                  css={{
                    color: '$blue8',
                    '&:hover': {
                      color: '$blue8',
                      textDecoration: 'underline'
                    }
                  }}
                  onClick={() => {
                    setIsDrawerOpen(true)
                    setSelectedAccountId(scoredAccount.accountId)
                  }}
                >
                  Go to {name} account
                </Link> 
              </Box>
            </Flex>
          </Flex>
        </>
      )}
    </Card>
  )
}

export default RecentActivityCard
